import React, { useState, useEffect, useMemo } from 'react';
import { NavArrowLeft, EditPencil, NavArrowRight } from 'iconoir-react';
import { C, St, CARD_STYLE, FONT_BODY, FONT_MONO, focus, blur } from '../theme';
import { _globalSettings, saveSettings } from '../constants';
import { EMPTY_DB, saveDB } from '../database';
import { ImportForm, CsvImportForm } from './Forms';
import { calcVolume, calcAvg1RM } from '../science';
import { supabase } from '../supabase';
import SciencePage from '../pages/SciencePage';
import ScienceTestPage from '../pages/ScienceTestPage';

// ─── SUPABASE SCHEMA (run once in Supabase SQL editor) ────────────────────────
// create table profiles (
//   user_id uuid primary key references auth.users(id) on delete cascade,
//   username text unique, display_name text, avatar_index integer default 1,
//   email text, created_at timestamptz default now()
// );
// alter table profiles enable row level security;
// create policy "view all" on profiles for select using (true);
// create policy "own insert" on profiles for insert with check (auth.uid()=user_id);
// create policy "own update" on profiles for update using (auth.uid()=user_id);
//
// create table friendships (
//   id uuid primary key default gen_random_uuid(),
//   requester_id uuid references profiles(user_id) on delete cascade,
//   addressee_id uuid references profiles(user_id) on delete cascade,
//   status text check (status in ('pending','accepted','rejected')) default 'pending',
//   created_at timestamptz default now(), unique(requester_id,addressee_id)
// );
// alter table friendships enable row level security;
// create policy "view own" on friendships for select using (auth.uid()=requester_id or auth.uid()=addressee_id);
// create policy "insert own" on friendships for insert with check (auth.uid()=requester_id);
// create policy "addressee update" on friendships for update using (auth.uid()=addressee_id);

const MEMO_COUNT = 35;
const memoUrl = n => `https://cdn.jsdelivr.net/gh/alohe/memojis@main/png/memo_${n}.png`;

const METRIC_INFO = [
  { id:'FR', name:'Forza Relativa',               desc:'Miglior 1RM stimato (Brzycki) diviso il tuo peso corporeo. 100 corrisponde a sollevare 2.5× il tuo peso.' },
  { id:'SS', name:'Slope Stability',              desc:'Coerenza del carico S1 nelle ultime 4 sessioni. Alta variabilità = punteggio basso. Indica regolarità dell\'intensità.' },
  { id:'DL', name:'Densità Lavorativa',           desc:'Volume della sessione rispetto al tuo record personale di sessione. 100 = sessione più voluminosa di sempre.' },
  { id:'CCS', name:'Carico Sostenuto',            desc:'Rapporto volume seconda metà / prima metà della sessione. Misura quanto mantieni il rendimento verso fine allenamento.' },
  { id:'EV', name:'Efficienza del Volume',        desc:'Reps stimolanti per serie (RIR < 5). 5 reps stimolanti per serie = 100. Indica quanto ogni serie è vicina al fallimento.' },
];

const ATTIVITA_OPTIONS = [
  { value:'1.2',   label:'Sedentario',             sub:'Poco o nessun esercizio' },
  { value:'1.375', label:'Leggermente attivo',      sub:'1-3 allenamenti a settimana' },
  { value:'1.55',  label:'Moderatamente attivo',    sub:'3-5 allenamenti a settimana' },
  { value:'1.725', label:'Molto attivo',             sub:'6-7 allenamenti a settimana' },
  { value:'1.9',   label:'Estremamente attivo',      sub:'Lavoro fisico + allenamento intenso' },
];

const OBIETTIVO_OPTIONS = [
  { value:'cut',    label:'Perdere peso' },
  { value:'recomp', label:'Mantenere il peso' },
  { value:'bulk',   label:'Prendere peso' },
];

// ─── LOCAL PROFILE STORAGE ────────────────────────────────────────────────────
function loadLocal(uid) {
  try { return JSON.parse(localStorage.getItem(`gp_profile_${uid}`)) || {}; } catch { return {}; }
}
function saveLocal(uid, patch) {
  try {
    const cur = loadLocal(uid);
    localStorage.setItem(`gp_profile_${uid}`, JSON.stringify({ ...cur, ...patch }));
  } catch {}
}

// ─── SUPABASE HELPERS (graceful degradation if tables missing) ────────────────
async function dbGetProfile(uid) {
  try { const { data } = await supabase.from('profiles').select('*').eq('user_id', uid).single(); return data; }
  catch { return null; }
}
async function dbUpsertProfile(uid, data) {
  try { await supabase.from('profiles').upsert({ user_id: uid, ...data }, { onConflict: 'user_id' }); }
  catch {}
}

// ─── TOGGLE ───────────────────────────────────────────────────────────────────
function IOSToggle({ value, onChange }) {
  return (
    <button onClick={onChange} style={{
      width: 51, height: 31, borderRadius: 16, border: 'none', cursor: 'pointer',
      padding: 0, flexShrink: 0, position: 'relative',
      background: value ? C.green : 'rgba(120,120,128,0.32)',
      transition: 'background 0.2s',
    }}>
      <div style={{
        position: 'absolute', top: 2, left: value ? 22 : 2,
        width: 27, height: 27, borderRadius: 14, background: '#fff',
        boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
        transition: 'left 0.2s',
      }}/>
    </button>
  );
}

// ─── RADIO BUTTON (iOS style) ─────────────────────────────────────────────────
function RadioRow({ label, sub, selected, onSelect }) {
  return (
    <button onClick={onSelect} style={{
      display:'flex', alignItems:'center', width:'100%',
      padding:'12px 0', background:'none', border:'none', cursor:'pointer',
      fontFamily:FONT_BODY, textAlign:'left',
    }}>
      <div style={{
        width:22, height:22, borderRadius:11, flexShrink:0,
        border: selected ? 'none' : `1.5px solid ${C.border}`,
        background: selected ? C.blue : 'transparent',
        display:'flex', alignItems:'center', justifyContent:'center',
        marginRight:14,
        transition:'background 0.15s, border 0.15s',
      }}>
        {selected && (
          <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
            <path d="M1 4.5L4.5 8L11 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:15, color:C.text, fontWeight:selected?600:400 }}>{label}</div>
        {sub && <div style={{ fontSize:12, color:C.subtle, marginTop:2 }}>{sub}</div>}
      </div>
    </button>
  );
}



// ─── SETTINGS ROW ─────────────────────────────────────────────────────────────
function SettingsRow({ label, value, onTap, isLast }) {
  return (
    <div style={{ position:'relative' }}>
      <button onClick={onTap} style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        width:'100%', padding:'14px 16px',
        background:'none', border:'none', cursor:'pointer',
        fontFamily:FONT_BODY, textAlign:'left',
      }}>
        <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
          <span style={{ fontSize:16, color:C.text, fontWeight:500 }}>{label}</span>
          <span style={{ fontSize:13, color:C.subtle }}>{value || 'Non impostato'}</span>
        </div>
        <NavArrowRight width={18} height={18} color={C.text} strokeWidth={2} style={{ flexShrink:0 }}/>
      </button>
      {!isLast && (
        <div style={{ height:'0.5px', background:'rgba(60,60,67,0.12)', marginLeft:16, marginRight:36 }}/>
      )}
    </div>
  );
}

// ─── SUB-PAGE CONTAINER ───────────────────────────────────────────────────────
function SubPage({ title, onBack, children }) {
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:450,
      background:'#FFFFFF', display:'flex', flexDirection:'column',
      overflowY:'auto',
    }}>
      {/* Header */}
      <div style={{
        position:'sticky', top:0, zIndex:10, background:'#FFFFFF',
        paddingTop:'max(52px, calc(env(safe-area-inset-top,0px) + 12px))',
        paddingBottom:6,
        borderBottom:'0.5px solid rgba(60,60,67,0.10)',
      }}>
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          paddingLeft:16, paddingRight:20, maxWidth:560, margin:'0 auto', position:'relative',
        }}>
          <button onClick={onBack} style={{
            display:'flex', alignItems:'center', gap:4,
            background:'none', border:'none', cursor:'pointer',
            color:C.blue, padding:'4px 0', fontFamily:FONT_BODY, fontSize:17, fontWeight:400,
          }}>
            <NavArrowLeft width={16} height={16}/>
            Indietro
          </button>
          <span style={{
            fontSize:17, fontWeight:600, color:C.text,
            position:'absolute', left:'50%', transform:'translateX(-50%)',
            pointerEvents:'none',
          }}>
            {title}
          </span>
          <div style={{ width:80 }}/>
        </div>
      </div>
      <div style={{ padding:'20px 20px 60px', maxWidth:560, margin:'0 auto', width:'100%' }}>
        {children}
      </div>
    </div>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export function UserArea({ user, db, setDB, onClose, onReplayOnboarding }) {
  const uid = user?.id;

  // Profile
  const [avatarIdx, setAvatarIdx] = useState(() => loadLocal(uid).avatarIndex || 1);
  const [displayName, setDisplayName] = useState(() => loadLocal(uid).displayName || '');
  const [username, setUsername] = useState(() => loadLocal(uid).username || '');
  const [profilo, setProfilo] = useState(() => ({ ..._globalSettings.profilo }));

  // Settings
  const [settings, setSettings] = useState(() => ({ ..._globalSettings }));
  const updateSettings = s => { setSettings(s); saveSettings(s); };
const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const exportData = () => { const a = document.createElement("a"); a.href = "data:application/json," + encodeURIComponent(JSON.stringify(db,null,2)); a.download = "gp_backup_"+new Date().toISOString().split("T")[0]+".json"; a.click(); };
  const deleteAll = () => { const e = {...EMPTY_DB}; setDB(e); saveDB(e); setShowDeleteConfirm(false); };

  // Sub-page navigation
  const [subPage, setSubPage] = useState(null); // null | 'genere' | 'dataNascita' | 'altezza' | 'peso' | 'obiettivo' | 'pesoTarget' | 'attivita' | 'science' | 'scienceTest'
  const [fieldDraft, setFieldDraft] = useState({});

  // Tab + sheets
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showMetricInfo, setShowMetricInfo] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  // Weight log data
  const weightData = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
    return [...(db.weightLog || [])].sort((a, b) => a.date.localeCompare(b.date))
      .filter(w => new Date(w.date) >= cutoff)
      .map(w => ({ date: new Date(w.date).toLocaleDateString('it-IT', { day:'numeric', month:'short' }), peso: w.weight }));
  }, [db.weightLog]);
  const lastW = weightData[weightData.length - 1]?.peso;

  // Add weight
  const [showAddWeight, setShowAddWeight] = useState(false);
  const [newWeightVal, setNewWeightVal] = useState('');
  const [newFatVal, setNewFatVal] = useState('');
  const [newLeanVal, setNewLeanVal] = useState('');
  const addWeight = () => {
    const w = parseFloat(newWeightVal.replace(',', '.'));
    if (!w || isNaN(w)) return;
    const fat = parseFloat(newFatVal.replace(',', '.'));
    const lean = parseFloat(newLeanVal.replace(',', '.'));
    const entry = {
      id: Date.now(), date: new Date().toISOString().split('T')[0], weight: w,
      ...(fat && !isNaN(fat) ? { fatPct: fat } : {}),
      ...(lean && !isNaN(lean) ? { leanMass: lean } : {}),
    };
    const newDb = { ...db, weightLog: [entry, ...(db.weightLog || [])] };
    setDB(newDb);
    saveDB(newDb);
    setNewWeightVal(''); setNewFatVal(''); setNewLeanVal('');
    setShowAddWeight(false);
  };

  // Stats
  const totalSessions = db.sessions.length;
  const totalVol = Math.round(db.sessions.reduce((s, sess) =>
    s + sess.exercises.reduce((t, e) => t + calcVolume(e.sets), 0), 0));
  const best1RM = db.sessions.reduce((best, sess) => {
    sess.exercises.forEach(e => { const rm = calcAvg1RM(e.sets); if (rm > best) best = rm; });
    return best;
  }, 0);
  // Load profile from Supabase on mount
  useEffect(() => {
    if (!uid) return;
    dbGetProfile(uid).then(p => {
      if (!p) return;
      setAvatarIdx(p.avatar_index || 1);
      setDisplayName(p.display_name || '');
      setUsername(p.username || '');
      saveLocal(uid, { avatarIndex: p.avatar_index || 1, displayName: p.display_name || '', username: p.username || '' });
    });
  }, [uid]);

  // Avatar select
  const pickAvatar = async n => {
    setAvatarIdx(n);
    setShowAvatarPicker(false);
    saveLocal(uid, { avatarIndex: n });
    window.dispatchEvent(new CustomEvent('gp:avatarChanged', { detail: { userId: uid, avatarIndex: n } }));
    await dbUpsertProfile(uid, { avatar_index: n, email: user?.email });
  };

  // Save profile
  const saveProfile = async () => {
    setSaving(true);
    const dn = (draft.displayName || '').trim();
    const un = (draft.username || '').trim().toLowerCase().replace(/[^a-z0-9_.]/g, '');
    setDisplayName(dn);
    setUsername(un);
    saveLocal(uid, { displayName: dn, username: un });
    await dbUpsertProfile(uid, {
      display_name: dn, username: un || null, email: user?.email, avatar_index: avatarIdx,
      eta:              draft.eta        ? parseInt(draft.eta)          : null,
      peso:             draft.peso       ? parseFloat(draft.peso)       : null,
      altezza:          draft.altezza    ? parseInt(draft.altezza)      : null,
      sesso:            draft.sesso      || 'M',
      peso_target:      draft.pesoTarget ? parseFloat(draft.pesoTarget) : null,
      livello_attivita: parseFloat(draft.livelloAttivita) || 1.55,
      esperienza:       draft.esperienza || null,
      obiettivo:        draft.obiettivo  || 'recomp',
    });
    const newProfilo = {
      ...profilo, peso: draft.peso, pesoTarget: draft.pesoTarget,
      altezza: draft.altezza, eta: draft.eta, sesso: draft.sesso,
      obiettivo: draft.obiettivo, esperienza: draft.esperienza,
      livelloAttivita: draft.livelloAttivita,
    };
    saveSettings({ ..._globalSettings, profilo: newProfilo });
    setProfilo(newProfilo);
    // Sync peso al weightLog se cambiato rispetto all'ultima entry
    const draftW = parseFloat(draft.peso);
    if (draftW && draftW !== lastW) {
      const entry = { id: Date.now(), date: new Date().toISOString().slice(0, 10), weight: draftW };
      const newDb = { ...db, weightLog: [...(db.weightLog || []), entry] };
      setDB(newDb);
      saveDB(newDb);
    }
    setSaving(false);
    setShowEdit(false);
  };

  // ─── SUB-PAGE: open helpers ───────────────────────────────────────────────
  const openSubPage = (page) => {
    let draft = {};
    if (page === 'genere')       draft = { sesso: profilo.sesso || 'M' };
    if (page === 'dataNascita')  draft = { dataNascita: profilo.dataNascita || '' };
    if (page === 'altezza')      draft = { altezza: profilo.altezza || '' };
    if (page === 'peso')         draft = {
      peso: profilo.peso || (lastW != null ? String(lastW) : ''),
      massaGrassa: profilo.massaGrassa || '',
      massaMagra: profilo.massaMagra || '',
    };
    if (page === 'obiettivo')    draft = { obiettivo: profilo.obiettivo || 'recomp' };
    if (page === 'pesoTarget')   draft = { pesoTarget: profilo.pesoTarget || '' };
    if (page === 'attivita')     draft = { livelloAttivita: profilo.livelloAttivita || '1.55' };
    setFieldDraft(draft);
    setSubPage(page);
  };

  // ─── SUB-PAGE: save and go back ──────────────────────────────────────────
  const saveSubPage = async () => {
    let newProfilo = { ...profilo };

    if (subPage === 'genere') {
      newProfilo.sesso = fieldDraft.sesso || 'M';
      await dbUpsertProfile(uid, { sesso: newProfilo.sesso, email: user?.email });
    }

    if (subPage === 'dataNascita') {
      newProfilo.dataNascita = fieldDraft.dataNascita || '';
      // compute eta for backward compat
      if (fieldDraft.dataNascita) {
        const dob = new Date(fieldDraft.dataNascita);
        const today = new Date();
        const age = today.getFullYear() - dob.getFullYear() -
          (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
        newProfilo.eta = String(age);
      }
    }

    if (subPage === 'altezza') {
      newProfilo.altezza = fieldDraft.altezza || '';
      await dbUpsertProfile(uid, { altezza: fieldDraft.altezza ? parseInt(fieldDraft.altezza) : null, email: user?.email });
    }

    if (subPage === 'peso') {
      const w = parseFloat(String(fieldDraft.peso).replace(',', '.'));
      const fat = parseFloat(String(fieldDraft.massaGrassa).replace(',', '.'));
      const computedLean = (w && fat && !isNaN(w) && !isNaN(fat)) ? Math.round(w * (1 - fat / 100) * 10) / 10 : null;
      newProfilo.peso = fieldDraft.peso || '';
      newProfilo.massaGrassa = fieldDraft.massaGrassa || '';
      newProfilo.massaMagra = computedLean != null ? String(computedLean) : (fieldDraft.massaMagra || '');
      await dbUpsertProfile(uid, { peso: w || null, email: user?.email });
      // Sync weightLog
      if (w && !isNaN(w)) {
        const entry = {
          id: Date.now(),
          date: new Date().toISOString().split('T')[0],
          weight: w,
          ...(fat && !isNaN(fat) ? { fatPct: fat } : {}),
          ...(computedLean != null ? { leanMass: computedLean } : {}),
        };
        const newDb = { ...db, weightLog: [entry, ...(db.weightLog || [])] };
        setDB(newDb);
        saveDB(newDb);
      }
      // Sync to localStorage profilo (bidirectional)
      try {
        const s = JSON.parse(localStorage.getItem('rc_settings') || '{}');
        s.profilo = { ...s.profilo, peso: String(w || ''), massaGrassa: fieldDraft.massaGrassa || '', massaMagra: computedLean != null ? String(computedLean) : '' };
        localStorage.setItem('rc_settings', JSON.stringify(s));
      } catch {}
    }

    if (subPage === 'obiettivo') {
      newProfilo.obiettivo = fieldDraft.obiettivo || 'recomp';
      await dbUpsertProfile(uid, { obiettivo: newProfilo.obiettivo, email: user?.email });
    }

    if (subPage === 'pesoTarget') {
      newProfilo.pesoTarget = fieldDraft.pesoTarget || '';
      await dbUpsertProfile(uid, { peso_target: fieldDraft.pesoTarget ? parseFloat(fieldDraft.pesoTarget) : null, email: user?.email });
    }

    if (subPage === 'attivita') {
      newProfilo.livelloAttivita = fieldDraft.livelloAttivita || '1.55';
      await dbUpsertProfile(uid, { livello_attivita: parseFloat(newProfilo.livelloAttivita), email: user?.email });
    }

    saveSettings({ ..._globalSettings, profilo: newProfilo });
    setProfilo(newProfilo);
    setSubPage(null);
  };

  // ─── DISPLAY VALUES ───────────────────────────────────────────────────────
  const displayGenere = profilo.sesso === 'M' ? 'Maschio' : profilo.sesso === 'F' ? 'Femmina' : null;

  const displayDataNascita = (() => {
    if (profilo.dataNascita) {
      const d = new Date(profilo.dataNascita + 'T00:00:00');
      return d.toLocaleDateString('it-IT', { day:'numeric', month:'short', year:'numeric' });
    }
    if (profilo.eta) return `${profilo.eta} anni`;
    return null;
  })();

  const displayAltezza = profilo.altezza ? `${profilo.altezza} cm` : null;

  const displayPeso = (() => {
    const w = profilo.peso || (lastW != null ? String(lastW) : '');
    if (!w) return null;
    const fat = profilo.massaGrassa;
    if (fat) return `${w} kg · ${fat}% BF`;
    return `${w} kg`;
  })();

  const displayObiettivo = (() => {
    const opt = OBIETTIVO_OPTIONS.find(o => o.value === profilo.obiettivo);
    return opt ? opt.label : null;
  })();

  const displayPesoTarget = profilo.pesoTarget ? `${profilo.pesoTarget} kg` : null;

  const displayAttivita = (() => {
    const opt = ATTIVITA_OPTIONS.find(o => o.value === profilo.livelloAttivita);
    return opt ? opt.label : null;
  })();

  // ─── LEAN MASS AUTO-CALC ─────────────────────────────────────────────────
  const autoLean = (() => {
    const w = parseFloat(String(fieldDraft.peso || '').replace(',', '.'));
    const fat = parseFloat(String(fieldDraft.massaGrassa || '').replace(',', '.'));
    if (w && fat && !isNaN(w) && !isNaN(fat)) {
      return Math.round((w * (1 - fat / 100)) * 10) / 10;
    }
    return null;
  })();

  const showName = displayName || user?.email?.split('@')[0] || 'Atleta';

  return (
    <div style={{ position:'fixed', inset:0, zIndex:400, background:C.bg, display:'flex', flexDirection:'column', overflowY:'auto' }}>

      {/* ─── Nav bar ────────────────────────────────────────────────── */}
      <div style={{ position:'sticky', top:0, zIndex:10, background:C.bg, paddingTop:'max(52px, calc(env(safe-area-inset-top,0px) + 12px))', paddingBottom:6 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingLeft:16, paddingRight:20, maxWidth:560, margin:'0 auto' }}>
          <button onClick={onClose} style={{ display:'flex', alignItems:'center', gap:4, background:'none', border:'none', cursor:'pointer', color:C.blue, padding:'4px 0', fontFamily:FONT_BODY, fontSize:17, fontWeight:400 }}>
            <NavArrowLeft width={16} height={16}/>
            Home
          </button>
          <span style={{ fontSize:17, fontWeight:600, color:C.text }}>Profilo</span>
          {/* Spacer to balance layout */}
          <div style={{ width:70 }}/>
        </div>
      </div>

      <div style={{ padding:'0 20px 60px', display:'flex', flexDirection:'column', gap:14, maxWidth:560, margin:'0 auto', width:'100%' }}>
        <style>{`@keyframes gpSlideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}`}</style>

        {/* ─── Profile hero ──────────────────────────────────────────── */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', paddingTop:12, paddingBottom:4, animation:'gpSlideUp 0.5s 0.0s cubic-bezier(0.22,1,0.36,1) both' }}>
          <div style={{ position:'relative', marginBottom:12 }}>
            <img src={memoUrl(avatarIdx)} alt="avatar"
              style={{ width:90, height:90, borderRadius:45, background:C.fill, display:'block' }}/>
            <button onClick={() => setShowAvatarPicker(true)}
              className="gp-btn-press"
              style={{ position:'absolute', bottom:1, right:1, width:28, height:28, borderRadius:14,
                background:C.blue, border:`2.5px solid ${C.bg}`, display:'flex', alignItems:'center',
                justifyContent:'center', cursor:'pointer' }}>
              <EditPencil width={12} height={12} color="#fff"/>
            </button>
          </div>
          <div style={{ fontSize:20, fontWeight:800, color:C.text, letterSpacing:-0.5 }}>{showName}</div>
          {username && <div style={{ fontSize:14, color:C.subtle, marginTop:3 }}>@{username}</div>}
          <div style={{ fontSize:12, color:C.muted, marginTop:4 }}>{user?.email}</div>
        </div>

        {/* ═══ PROFILO — iOS Settings rows ══════════════════════════════ */}
        <div style={{ fontSize:13, fontWeight:600, color:C.subtle, fontFamily:FONT_BODY, textTransform:'uppercase', letterSpacing:0.4, marginTop:16, marginBottom:6 }}>Profilo</div>
        <div style={{ ...CARD_STYLE, overflow:'hidden', padding:'16px' }}>
          {[
            { key:'genere',      label:'Genere',           value:displayGenere },
            { key:'dataNascita', label:'Data di nascita',  value:displayDataNascita },
            { key:'altezza',     label:'Altezza',          value:displayAltezza },
            { key:'peso',        label:'Peso attuale',     value:displayPeso },
            { key:'obiettivo',   label:'Obiettivo',        value:displayObiettivo },
            { key:'pesoTarget',  label:'Peso obiettivo',   value:displayPesoTarget },
            { key:'attivita',    label:'Livello attivita', value:displayAttivita },
          ].map((row, i, arr) => (
            <SettingsRow
              key={row.key}
              label={row.label}
              value={row.value}
              onTap={() => openSubPage(row.key)}
              isLast={i === arr.length - 1}
            />
          ))}
        </div>


        {/* ═══ Statistiche ═══════════════════════════════════════════════ */}
        {totalSessions > 0 && (
          <>
            <div style={{ fontSize:13, fontWeight:600, color:C.subtle, fontFamily:FONT_BODY, textTransform:'uppercase', letterSpacing:0.4, marginTop:16, marginBottom:6 }}>Statistiche</div>
          <div style={St.card}>
            {[
              { label:'Sessioni totali',      value: String(totalSessions) },
              { label:'Volume totale',        value: totalVol.toLocaleString() },
              best1RM > 0 && { label:'Miglior 1RM stimato', value:`${Math.round(best1RM)} kg` },
            ].filter(Boolean).map((r, i, arr) => (
              <div key={r.label}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0' }}>
                  <span style={{ fontSize:15, color:C.subtle, fontFamily:FONT_BODY }}>{r.label}</span>
                  <span style={{ fontSize:15, fontWeight:700, color:C.text, fontFamily:FONT_BODY }}>{r.value}</span>
                </div>
                {i < arr.length - 1 && <div style={{ height:'0.5px', background:'rgba(60,60,67,0.12)' }}/>}
              </div>
            ))}
          </div>
          </>
        )}


        {/* ═══ IMPOSTAZIONI ════════════════════════════════════════════ */}
        {true && <>

          {/* Tracciamento intensità */}
          <div style={{ fontSize:13, fontWeight:600, color:C.subtle, fontFamily:FONT_BODY, textTransform:'uppercase', letterSpacing:0.4, marginTop:16, marginBottom:6 }}>Tracciamento intensità</div>
          <div style={St.card}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:settings.intensitaAttiva!==false?`0.5px solid ${C.border}`:'none' }}>
              <div>
                <div style={{ fontSize:15, fontWeight:600, color:C.text }}>Attivo</div>
                <div style={{ fontSize:12, color:C.subtle, marginTop:2 }}>Mostra colonne RPE/RIR nelle serie</div>
              </div>
              <IOSToggle value={settings.intensitaAttiva !== false} onChange={() => updateSettings({ ...settings, intensitaAttiva: !settings.intensitaAttiva })} />
            </div>
            {settings.intensitaAttiva !== false && (
              <div style={{ paddingTop:12 }}>
                <div style={{ fontSize:12, color:C.subtle, marginBottom:8 }}>Modalità di tracciamento</div>
                <div style={{ display:'flex', background:C.fill, borderRadius:10, padding:2 }}>
                  {[{ v:'rpe', l:'Solo RPE' }, { v:'rir', l:'Solo RIR' }, { v:'entrambi', l:'Entrambi' }].map(({ v, l }) => (
                    <button key={v} onClick={() => updateSettings({ ...settings, intensita: v })}
                      style={{ flex:1, padding:'8px 0', borderRadius:8, border:'none', cursor:'pointer', fontFamily:FONT_BODY, fontSize:12,
                        fontWeight:settings.intensita===v?600:400,
                        background:settings.intensita===v?C.card:'transparent',
                        color:settings.intensita===v?C.text:C.subtle,
                        boxShadow:settings.intensita===v?'0 1px 3px rgba(0,0,0,0.12)':'none', transition:'all 0.15s' }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Deload automatico */}
          <div style={{ fontSize:13, fontWeight:600, color:C.subtle, fontFamily:FONT_BODY, textTransform:'uppercase', letterSpacing:0.4, marginTop:16, marginBottom:6 }}>Recupero</div>
          <div style={St.card}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0' }}>
              <div>
                <div style={{ fontSize:15, fontWeight:600, color:C.text }}>Deload automatico</div>
                <div style={{ fontSize:12, color:C.subtle, marginTop:2 }}>Suggerisce una settimana di scarico ogni 5-6 settimane</div>
              </div>
              <IOSToggle value={settings.deloadAuto !== false} onChange={() => updateSettings({ ...settings, deloadAuto: !settings.deloadAuto })} />
            </div>
          </div>

          {/* Range reps */}
          <div style={{ fontSize:13, fontWeight:600, color:C.subtle, fontFamily:FONT_BODY, textTransform:'uppercase', letterSpacing:0.4, marginTop:16, marginBottom:6 }}>Range reps</div>
          <div style={St.card}>
            <div style={{ fontSize:12, color:C.subtle, marginBottom:12, lineHeight:1.5 }}>Usato per i suggerimenti di progressione quando RPE non viene tracciato.</div>
            {[['compound','Compound'],['isolation','Isolation']].map(([k, label]) => {
              const cur = (settings.repRange||{})[k] || { min: k==='compound'?6:10, max: k==='compound'?10:15 };
              return (
                <div key={k} style={{ marginBottom:12 }}>
                  <div style={{ fontSize:13, fontWeight:500, color:C.text, marginBottom:8 }}>{label}</div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    {[['min','Min'],['max','Max']].map(([field, fieldLabel], fi) => (
                      <React.Fragment key={field}>
                        {fi === 1 && <div style={{ fontSize:14, color:C.subtle }}>–</div>}
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:11, color:C.subtle, marginBottom:4 }}>{fieldLabel}</div>
                          <input type="number" min={1} max={50} value={cur[field]}
                            onChange={e => {
                              const v = Math.max(1, parseInt(e.target.value)||1);
                              updateSettings({ ...settings, repRange: { ...(settings.repRange||{}), [k]: { ...cur, [field]: v } } });
                            }}
                            style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:15, fontFamily:FONT_BODY, background:C.fill, color:C.text, textAlign:'center' }}
                          />
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* App */}
          <div style={{ fontSize:13, fontWeight:600, color:C.subtle, fontFamily:FONT_BODY, textTransform:'uppercase', letterSpacing:0.4, marginTop:16, marginBottom:6 }}>App</div>
          <div style={St.card}>
            <button onClick={() => setSubPage('science')}
              style={{ display:'flex', justifyContent:'space-between', alignItems:'center', width:'100%',
                padding:'12px 0', border:'none', background:'none', cursor:'pointer', fontFamily:FONT_BODY }}>
              <span style={{ fontSize:15, color:C.text }}>Come funziona l'analisi</span>
              <NavArrowRight width={14} height={14} color={C.border} strokeWidth={1.5}/>
            </button>
            {process.env.NODE_ENV === 'development' && (
              <>
                <div style={{ height:'0.5px', background:C.border }} />
                <button onClick={() => setSubPage('scienceTest')}
                  style={{ display:'flex', justifyContent:'space-between', alignItems:'center', width:'100%',
                    padding:'12px 0', border:'none', background:'none', cursor:'pointer', fontFamily:FONT_BODY }}>
                  <span style={{ fontSize:15, color:C.subtle }}>Test scienze (dev)</span>
                  <NavArrowRight width={14} height={14} color={C.border} strokeWidth={1.5}/>
                </button>
              </>
            )}
          </div>

          {/* Dati */}
          <div style={{ fontSize:13, fontWeight:600, color:C.subtle, fontFamily:FONT_BODY, textTransform:'uppercase', letterSpacing:0.4, marginTop:16, marginBottom:6 }}>Dati</div>
          <div style={St.card}>
            {[
              { label:'Importa JSON',        action: () => setShowImport(true) },
              { label:'Importa CSV (Hevy)',   action: () => setShowCsvImport(true) },
              { label:'Esporta backup JSON',  action: exportData },
            ].map((item, i, arr) => (
              <button key={item.label} onClick={item.action}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center', width:'100%',
                  padding:'12px 0', border:'none', borderBottom: i < arr.length - 1 ? `0.5px solid ${C.border}` : 'none',
                  background:'none', cursor:'pointer', fontFamily:FONT_BODY }}>
                <span style={{ fontSize:15, color:C.text }}>{item.label}</span>
                <NavArrowRight width={14} height={14} color={C.border} strokeWidth={1.5}/>
              </button>
            ))}
            <button onClick={() => setShowDeleteConfirm(true)}
              style={{ display:'flex', width:'100%', padding:'12px 0', background:'none', border:'none', cursor:'pointer', fontFamily:FONT_BODY }}>
              <span style={{ fontSize:15, color:C.red }}>Elimina tutti i dati</span>
            </button>
          </div>

          {/* Account */}
          <div style={{ fontSize:13, fontWeight:600, color:C.subtle, fontFamily:FONT_BODY, textTransform:'uppercase', letterSpacing:0.4, marginTop:16, marginBottom:6 }}>Account</div>
          <div style={St.card}>
            {onReplayOnboarding && (
              <>
                <button onClick={() => { onClose(); onReplayOnboarding(); }}
                  style={{ display:'flex', width:'100%', padding:'12px 0', background:'none', border:'none', cursor:'pointer', fontFamily:FONT_BODY }}>
                  <span style={{ fontSize:15, color:C.blue }}>Rivedi il tutorial</span>
                </button>
                <div style={{ height:'0.5px', background:C.border, margin:'0' }} />
              </>
            )}
            <button onClick={() => setShowLogoutConfirm(true)}
              style={{ display:'flex', width:'100%', padding:'12px 0', background:'none', border:'none', cursor:'pointer', fontFamily:FONT_BODY }}>
              <span style={{ fontSize:15, color:C.red }}>Esci dall'account</span>
            </button>
          </div>

          {/* Import JSON sheet */}
          {showImport && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:600, display:'flex', alignItems:'flex-end' }}
              onClick={e => { if (e.target===e.currentTarget) setShowImport(false); }}>
              <div style={{ background:C.card, borderRadius:'16px 16px 0 0', width:'100%', maxWidth:560, margin:'0 auto', padding:'20px 20px 48px' }}>
                <ImportForm db={db} onSave={newDb => { setDB(newDb); saveDB(newDb); setShowImport(false); }} />
              </div>
            </div>
          )}

          {/* Import CSV sheet */}
          {showCsvImport && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:600, display:'flex', alignItems:'flex-end' }}
              onClick={e => { if (e.target===e.currentTarget) setShowCsvImport(false); }}>
              <div style={{ background:C.card, borderRadius:'16px 16px 0 0', width:'100%', maxWidth:560, margin:'0 auto', padding:'20px 20px 48px' }}>
                <CsvImportForm db={db} onSave={async newDb => { setDB(newDb); await saveDB(newDb); setShowCsvImport(false); }} />
              </div>
            </div>
          )}

          {/* Logout confirm */}
          {showLogoutConfirm && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:600, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
              onClick={e => { if (e.target===e.currentTarget) setShowLogoutConfirm(false); }}>
              <div style={{ background:C.card, borderRadius:12, padding:'24px 20px', maxWidth:320, width:'100%', border:'1px solid #EAEAEA', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize:17, fontWeight:700, color:C.text, marginBottom:8 }}>Esci dall'account?</div>
                <div style={{ fontSize:14, color:C.subtle, marginBottom:20, lineHeight:1.6 }}>
                  I tuoi dati rimarranno salvati. Potrai accedere di nuovo in qualsiasi momento.
                </div>
                <button onClick={async () => { setShowLogoutConfirm(false); await supabase.auth.signOut(); onClose(); }}
                  style={{ ...St.btn(C.red), width:'100%', padding:'13px', borderRadius:8, fontSize:15, marginBottom:10 }}>
                  Esci
                </button>
                <button onClick={() => setShowLogoutConfirm(false)}
                  style={{ width:'100%', padding:'13px', borderRadius:8, fontSize:15, background:C.fill, border:'none', cursor:'pointer', fontFamily:FONT_BODY, fontWeight:600, color:C.text }}>
                  Annulla
                </button>
              </div>
            </div>
          )}

          {/* Delete confirm */}
          {showDeleteConfirm && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:600, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
              onClick={e => { if (e.target===e.currentTarget) setShowDeleteConfirm(false); }}>
              <div style={{ background:C.card, borderRadius:12, padding:'24px 20px', maxWidth:320, width:'100%', border:'1px solid #EAEAEA', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize:17, fontWeight:700, color:C.text, marginBottom:8 }}>Elimina tutti i dati</div>
                <div style={{ fontSize:14, color:C.subtle, marginBottom:20, lineHeight:1.6 }}>
                  Questa azione eliminerà sessioni, log del peso e schede. Non è reversibile.
                </div>
                <button onClick={deleteAll}
                  style={{ ...St.btn(C.red), width:'100%', padding:'13px', borderRadius:8, fontSize:15, marginBottom:10 }}>
                  Elimina tutto
                </button>
                <button onClick={() => setShowDeleteConfirm(false)}
                  style={{ width:'100%', padding:'13px', borderRadius:8, fontSize:15, background:C.fill, border:'none', cursor:'pointer', fontFamily:FONT_BODY, fontWeight:600, color:C.text }}>
                  Annulla
                </button>
              </div>
            </div>
          )}
        </>}

      </div>{/* end scroll content */}

      {/* ─── Add Weight Sheet ──────────────────────────────────────── */}
      {showAddWeight && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.28)', zIndex:500, display:'flex', alignItems:'flex-end' }}
          onClick={e => { if (e.target === e.currentTarget) setShowAddWeight(false); }}>
          <div style={{ background:'#F5F2ED', borderRadius:'16px 16px 0 0', width:'100%', maxWidth:560, margin:'0 auto',
            paddingBottom:'max(32px,env(safe-area-inset-bottom,16px))', maxHeight:'92vh', overflowY:'auto' }}>
            {/* Drag handle */}
            <div style={{ width:36, height:4, borderRadius:2, background:'rgba(120,120,128,0.28)', margin:'12px auto 0' }}/>
            {/* Nav row */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px' }}>
              <button onClick={() => setShowAddWeight(false)}
                style={{ background:'none', border:'none', color:C.blue, fontSize:17, cursor:'pointer', fontFamily:FONT_BODY, padding:0 }}>
                Annulla
              </button>
              <span style={{ fontSize:17, fontWeight:600, color:C.text }}>Registra peso</span>
              <button onClick={addWeight}
                style={{ background:'none', border:'none', color:!newWeightVal?C.subtle:C.blue, fontSize:17, fontWeight:600, cursor:'pointer', fontFamily:FONT_BODY, padding:0 }}>
                Salva
              </button>
            </div>
            <div style={{ padding:'0 20px 8px', display:'flex', flexDirection:'column', gap:12 }}>
              {/* Big weight input */}
              <div style={{ background:'#fff', borderRadius:10, border:'1px solid #EAEAEA', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', padding:'16px 20px' }}>
                <div style={St.label}>Peso corporeo</div>
                <input
                  style={{ width:'100%', border:'none', outline:'none', background:'transparent', fontFamily:FONT_BODY, fontSize:36, fontWeight:700, color:C.text, letterSpacing:-1.5, boxSizing:'border-box' }}
                  type="number" step="0.1" min="30" max="300" placeholder="75.0"
                  value={newWeightVal}
                  onChange={e => setNewWeightVal(e.target.value)}
                  autoFocus
                />
                <div style={{ fontSize:12, color:C.muted, marginTop:4 }}>
                  {new Date().toLocaleDateString('it-IT', { weekday:'long', day:'numeric', month:'long' })}
                </div>
              </div>
              {/* Fat % + Lean mass row */}
              <div style={{ display:'flex', gap:10 }}>
                <div style={{ flex:1, background:'#fff', borderRadius:10, border:'1px solid #EAEAEA', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', padding:'14px 16px' }}>
                  <div style={{ ...St.label, color:C.purple }}>Body Fat %</div>
                  <input
                    style={{ width:'100%', border:'none', outline:'none', background:'transparent', fontFamily:FONT_BODY, fontSize:22, fontWeight:700, color:C.text, letterSpacing:-0.5, boxSizing:'border-box' }}
                    type="number" step="0.1" min="3" max="60" placeholder="—"
                    value={newFatVal}
                    onChange={e => setNewFatVal(e.target.value)}
                  />
                  <div style={{ fontSize:11, color:C.muted }}>opzionale</div>
                </div>
                <div style={{ flex:1, background:'#fff', borderRadius:10, border:'1px solid #EAEAEA', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', padding:'14px 16px' }}>
                  <div style={{ ...St.label, color:C.blue }}>Massa magra kg</div>
                  <input
                    style={{ width:'100%', border:'none', outline:'none', background:'transparent', fontFamily:FONT_BODY, fontSize:22, fontWeight:700, color:C.text, letterSpacing:-0.5, boxSizing:'border-box' }}
                    type="number" step="0.1" min="20" max="200" placeholder="—"
                    value={newLeanVal}
                    onChange={e => setNewLeanVal(e.target.value)}
                  />
                  <div style={{ fontSize:11, color:C.muted }}>opzionale</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Avatar Picker Sheet ────────────────────────────────────── */}
      {showAvatarPicker && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:500, display:'flex', alignItems:'flex-end' }}
          onClick={e => { if (e.target===e.currentTarget) setShowAvatarPicker(false); }}>
          <div style={{ background:C.card, borderRadius:'16px 16px 0 0', width:'100%', maxWidth:560, margin:'0 auto', padding:'20px 20px 48px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ fontSize:17, fontWeight:700, color:C.text }}>Scegli avatar</div>
              <button onClick={() => setShowAvatarPicker(false)}
                style={{ width:30, height:30, borderRadius:15, background:C.fill, border:'none', fontSize:18, cursor:'pointer', color:C.subtle, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10 }}>
              {Array.from({ length: MEMO_COUNT }, (_, i) => i + 1).map(n => (
                <button key={n} onClick={() => pickAvatar(n)}
                  style={{ background:'none', border:'none', cursor:'pointer', padding:3, borderRadius:16,
                    outline:avatarIdx===n?`3px solid ${C.blue}`:'none', outlineOffset:2 }}>
                  <img src={memoUrl(n)} alt={`memo ${n}`}
                    style={{ width:'100%', aspectRatio:'1/1', borderRadius:13, background:C.fill, display:'block' }}/>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── Metric Info Sheet ─────────────────────────────────────── */}
      {showMetricInfo && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:600, display:'flex', alignItems:'flex-end' }}
          onClick={e => { if (e.target===e.currentTarget) setShowMetricInfo(false); }}>
          <div style={{ background:C.card, borderRadius:'16px 16px 0 0', width:'100%', maxWidth:560, margin:'0 auto', padding:'20px 20px 48px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ fontSize:17, fontWeight:700, color:C.text }}>Come si calcolano</div>
              <button onClick={() => setShowMetricInfo(false)}
                style={{ width:30, height:30, borderRadius:15, background:C.fill, border:'none', fontSize:18, cursor:'pointer', color:C.subtle, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
            </div>
            {METRIC_INFO.map((m, i, arr) => (
              <div key={m.id} style={{ padding:'12px 0', borderBottom:i<arr.length-1?`0.5px solid ${C.border}`:'none' }}>
                <div style={{ display:'flex', alignItems:'baseline', gap:8, marginBottom:5 }}>
                  <span style={{ fontSize:15, fontWeight:800, color:C.blue, fontFamily:FONT_MONO }}>{m.id}</span>
                  <span style={{ fontSize:14, fontWeight:600, color:C.text }}>{m.name}</span>
                </div>
                <div style={{ fontSize:13, color:C.subtle, lineHeight:1.65 }}>{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Edit Profile Sheet (kept, not triggered from navbar) ──── */}
      {showEdit && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:500, display:'flex', alignItems:'flex-end' }}
          onClick={e => { if (e.target===e.currentTarget) setShowEdit(false); }}>
          <div style={{ background:C.card, borderRadius:'16px 16px 0 0', width:'100%', maxWidth:560, margin:'0 auto',
            maxHeight:'92vh', overflowY:'auto', padding:'20px 20px 48px' }}>

            {/* Sheet nav */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
              <button onClick={() => setShowEdit(false)}
                style={{ background:'none', border:'none', color:C.subtle, fontSize:15, cursor:'pointer', fontFamily:FONT_BODY, padding:0 }}>
                Annulla
              </button>
              <div style={{ fontSize:17, fontWeight:700, color:C.text }}>Modifica profilo</div>
              <button onClick={saveProfile} disabled={saving}
                style={{ background:'none', border:'none', color:C.blue, fontSize:15, fontWeight:600, cursor:'pointer', fontFamily:FONT_BODY, padding:0, opacity:saving?0.5:1 }}>
                {saving ? 'Salvo…' : 'Salva'}
              </button>
            </div>

            {/* Display name */}
            <label style={St.label}>Nome visualizzato</label>
            <input style={{ ...St.input, marginBottom:14 }} placeholder="Il tuo nome"
              value={draft.displayName || ''}
              onChange={e => setDraft(d => ({ ...d, displayName: e.target.value }))}
              onFocus={focus} onBlur={blur}/>

            {/* Username */}
            <label style={St.label}>Username</label>
            <div style={{ position:'relative', marginBottom:20 }}>
              <span style={{ position:'absolute', left:16, top:'50%', transform:'translateY(-50%)', color:C.muted, fontSize:15, fontWeight:500 }}>@</span>
              <input style={{ ...St.input, paddingLeft:30 }} placeholder="username"
                value={draft.username || ''}
                onChange={e => setDraft(d => ({ ...d, username: e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, '') }))}
                onFocus={focus} onBlur={blur}/>
            </div>

            {/* Divider */}
            <div style={{ height:'0.5px', background:C.border, margin:'4px 0 20px' }}/>
            <div style={{ fontSize:11, fontWeight:700, color:C.subtle, letterSpacing:0.8, textTransform:'uppercase', marginBottom:14 }}>Dati atletici</div>

            {/* Numeric fields */}
            {[
              { key:'peso',       label:'Peso (kg)',        ph:'es. 75',  step:'0.5' },
              { key:'pesoTarget', label:'Peso target (kg)', ph:'es. 70',  step:'0.5' },
              { key:'altezza',    label:'Altezza (cm)',     ph:'es. 175', step:'1' },
              { key:'eta',        label:'Età',              ph:'es. 25',  step:'1' },
            ].map(f => (
              <div key={f.key}>
                <label style={St.label}>{f.label}</label>
                <input style={{ ...St.input, marginBottom:14 }} type="number" step={f.step} placeholder={f.ph}
                  value={draft[f.key] || ''}
                  onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
                  onFocus={focus} onBlur={blur}/>
              </div>
            ))}

            {/* Sesso */}
            <label style={St.label}>Sesso</label>
            <div style={{ display:'flex', background:C.fill, borderRadius:10, padding:2, marginBottom:14 }}>
              {[{ v:'M', l:'Maschio' }, { v:'F', l:'Femmina' }].map(({ v, l }) => (
                <button key={v} onClick={() => setDraft(d => ({ ...d, sesso: v }))}
                  style={{ flex:1, padding:'8px 0', borderRadius:8, border:'none', cursor:'pointer', fontFamily:FONT_BODY, fontSize:14, fontWeight:draft.sesso===v?600:400,
                    background:draft.sesso===v?C.card:'transparent', color:draft.sesso===v?C.text:C.subtle,
                    boxShadow:draft.sesso===v?'0 1px 3px rgba(0,0,0,0.12)':'none', transition:'all 0.15s' }}>
                  {l}
                </button>
              ))}
            </div>

            {/* Obiettivo */}
            <label style={St.label}>Obiettivo</label>
            <div style={{ display:'flex', background:C.fill, borderRadius:10, padding:2, marginBottom:14 }}>
              {[{ v:'cut', l:'Cut' }, { v:'recomp', l:'Recomp' }, { v:'bulk', l:'Bulk' }].map(({ v, l }) => (
                <button key={v} onClick={() => setDraft(d => ({ ...d, obiettivo: v }))}
                  style={{ flex:1, padding:'8px 0', borderRadius:8, border:'none', cursor:'pointer', fontFamily:FONT_BODY, fontSize:14, fontWeight:draft.obiettivo===v?600:400,
                    background:draft.obiettivo===v?C.card:'transparent', color:draft.obiettivo===v?C.text:C.subtle,
                    boxShadow:draft.obiettivo===v?'0 1px 3px rgba(0,0,0,0.12)':'none', transition:'all 0.15s' }}>
                  {l}
                </button>
              ))}
            </div>

            {/* Esperienza */}
            <label style={St.label}>Livello di esperienza</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
              {['Principiante', 'Intermedio', 'Avanzato'].map(v => (
                <button key={v} onClick={() => setDraft(d => ({ ...d, esperienza: v }))}
                  style={{ padding:'8px 18px', borderRadius:20, cursor:'pointer', fontFamily:FONT_BODY, fontSize:13, fontWeight:500, transition:'all 0.15s',
                    background:draft.esperienza===v?C.blue:'transparent',
                    color:draft.esperienza===v?'#fff':C.subtle,
                    border:draft.esperienza===v?'none':`1.5px solid ${C.border}` }}>
                  {v}
                </button>
              ))}
            </div>

            {/* Livello di attività */}
            <label style={St.label}>Livello di attività</label>
            <select style={{ ...St.input, marginBottom:14, appearance:'none', WebkitAppearance:'none' }}
              value={draft.livelloAttivita || '1.55'}
              onChange={e => setDraft(d => ({ ...d, livelloAttivita: e.target.value }))}>
              <option value="1.2">Sedentario – poco o nessun esercizio</option>
              <option value="1.375">Leggermente attivo – 1-3 volte/settimana</option>
              <option value="1.55">Moderatamente attivo – 3-5 volte/settimana</option>
              <option value="1.725">Molto attivo – 6-7 volte/settimana</option>
              <option value="1.9">Estremamente attivo – lavoro fisico + allenamento</option>
            </select>

          </div>
        </div>
      )}

      {/* ─── Sub-pages ─────────────────────────────────────────────── */}
      {subPage === 'genere' && (
        <SubPage title="Genere" onBack={saveSubPage}>
          <div style={{ background:'#fff', borderRadius:16, border:'1px solid rgba(0,0,0,0.06)', overflow:'hidden', padding:'0 16px' }}>
            {[{ v:'M', l:'Maschio' }, { v:'F', l:'Femmina' }].map((opt, i, arr) => (
              <div key={opt.v} style={{ borderBottom: i < arr.length-1 ? '0.5px solid rgba(60,60,67,0.10)' : 'none' }}>
                <RadioRow
                  label={opt.l}
                  selected={fieldDraft.sesso === opt.v}
                  onSelect={() => setFieldDraft(d => ({ ...d, sesso: opt.v }))}
                />
              </div>
            ))}
          </div>
        </SubPage>
      )}

      {subPage === 'dataNascita' && (
        <SubPage title="Data di nascita" onBack={saveSubPage}>
          <div style={{ background:'#fff', borderRadius:16, border:'1px solid rgba(0,0,0,0.06)', overflow:'hidden' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px' }}>
              <span style={{ fontSize:15, color:C.text }}>Data di nascita</span>
              <input
                type="date"
                value={fieldDraft.dataNascita || ''}
                onChange={e => setFieldDraft(d => ({ ...d, dataNascita: e.target.value }))}
                style={{
                  border:'none', outline:'none', background:'transparent',
                  fontFamily:FONT_BODY, fontSize:15, color:C.blue,
                  textAlign:'right', cursor:'pointer',
                }}
              />
            </div>
          </div>
          {fieldDraft.dataNascita && (
            <div style={{ marginTop:16, padding:'12px 16px', background:'#fff', borderRadius:16, border:'1px solid rgba(0,0,0,0.06)' }}>
              <span style={{ fontSize:14, color:C.subtle }}>
                {(() => {
                  const dob = new Date(fieldDraft.dataNascita + 'T00:00:00');
                  const today = new Date();
                  const age = today.getFullYear() - dob.getFullYear() -
                    (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
                  return `Eta calcolata: ${age} anni`;
                })()}
              </span>
            </div>
          )}
        </SubPage>
      )}

      {subPage === 'altezza' && (
        <SubPage title="Altezza" onBack={saveSubPage}>
          <div style={{ background:'#fff', borderRadius:16, border:'1px solid rgba(0,0,0,0.06)', overflow:'hidden' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px' }}>
              <span style={{ fontSize:15, color:C.text }}>Altezza</span>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <input
                  type="number"
                  step="1"
                  min="100"
                  max="250"
                  placeholder="175"
                  value={fieldDraft.altezza || ''}
                  onChange={e => setFieldDraft(d => ({ ...d, altezza: e.target.value }))}
                  style={{
                    border:'none', outline:'none', background:'transparent',
                    fontFamily:FONT_BODY, fontSize:22, fontWeight:600,
                    color:C.text, width:80, textAlign:'right',
                  }}
                  autoFocus
                />
                <span style={{ fontSize:15, color:C.subtle }}>cm</span>
              </div>
            </div>
          </div>
        </SubPage>
      )}

      {subPage === 'peso' && (
        <SubPage title="Peso attuale" onBack={saveSubPage}>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {/* Peso */}
            <div style={{ background:'#fff', borderRadius:16, border:'1px solid rgba(0,0,0,0.06)', overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px' }}>
                <span style={{ fontSize:15, color:C.text }}>Peso</span>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <input
                    type="number"
                    step="0.1"
                    min="30"
                    max="300"
                    placeholder="75.0"
                    value={fieldDraft.peso || ''}
                    onChange={e => setFieldDraft(d => ({ ...d, peso: e.target.value }))}
                    style={{
                      border:'none', outline:'none', background:'transparent',
                      fontFamily:FONT_BODY, fontSize:22, fontWeight:600,
                      color:C.text, width:80, textAlign:'right',
                    }}
                    autoFocus
                  />
                  <span style={{ fontSize:15, color:C.subtle }}>kg</span>
                </div>
              </div>
            </div>
            {/* Massa grassa */}
            <div style={{ background:'#fff', borderRadius:16, border:'1px solid rgba(0,0,0,0.06)', overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderBottom:'0.5px solid rgba(60,60,67,0.10)' }}>
                <div>
                  <div style={{ fontSize:15, color:C.text }}>Massa grassa</div>
                  <div style={{ fontSize:12, color:C.subtle, marginTop:2 }}>opzionale</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <input
                    type="number"
                    step="0.1"
                    min="3"
                    max="60"
                    placeholder="—"
                    value={fieldDraft.massaGrassa || ''}
                    onChange={e => setFieldDraft(d => ({ ...d, massaGrassa: e.target.value }))}
                    style={{
                      border:'none', outline:'none', background:'transparent',
                      fontFamily:FONT_BODY, fontSize:22, fontWeight:600,
                      color:C.text, width:60, textAlign:'right',
                    }}
                  />
                  <span style={{ fontSize:15, color:C.subtle }}>%</span>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px' }}>
                <div>
                  <div style={{ fontSize:15, color:C.text }}>Massa magra</div>
                  <div style={{ fontSize:12, color:C.subtle, marginTop:2 }}>
                    {autoLean != null ? `Calcolata: ${autoLean} kg` : 'opzionale, calcolata auto'}
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <input
                    type="number"
                    step="0.1"
                    min="20"
                    max="200"
                    placeholder={autoLean != null ? String(autoLean) : '—'}
                    value={fieldDraft.massaMagra || ''}
                    onChange={e => setFieldDraft(d => ({ ...d, massaMagra: e.target.value }))}
                    style={{
                      border:'none', outline:'none', background:'transparent',
                      fontFamily:FONT_BODY, fontSize:22, fontWeight:600,
                      color:autoLean != null && !fieldDraft.massaMagra ? C.subtle : C.text,
                      width:80, textAlign:'right',
                    }}
                  />
                  <span style={{ fontSize:15, color:C.subtle }}>kg</span>
                </div>
              </div>
            </div>
          </div>
        </SubPage>
      )}

      {subPage === 'obiettivo' && (
        <SubPage title="Obiettivo" onBack={saveSubPage}>
          <div style={{ background:'#fff', borderRadius:16, border:'1px solid rgba(0,0,0,0.06)', overflow:'hidden', padding:'0 16px' }}>
            {OBIETTIVO_OPTIONS.map((opt, i, arr) => (
              <div key={opt.value} style={{ borderBottom: i < arr.length-1 ? '0.5px solid rgba(60,60,67,0.10)' : 'none' }}>
                <RadioRow
                  label={opt.label}
                  selected={fieldDraft.obiettivo === opt.value}
                  onSelect={() => setFieldDraft(d => ({ ...d, obiettivo: opt.value }))}
                />
              </div>
            ))}
          </div>
        </SubPage>
      )}

      {subPage === 'pesoTarget' && (
        <SubPage title="Peso obiettivo" onBack={saveSubPage}>
          <div style={{ background:'#fff', borderRadius:16, border:'1px solid rgba(0,0,0,0.06)', overflow:'hidden' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px' }}>
              <span style={{ fontSize:15, color:C.text }}>Peso obiettivo</span>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <input
                  type="number"
                  step="0.5"
                  min="30"
                  max="300"
                  placeholder="70"
                  value={fieldDraft.pesoTarget || ''}
                  onChange={e => setFieldDraft(d => ({ ...d, pesoTarget: e.target.value }))}
                  style={{
                    border:'none', outline:'none', background:'transparent',
                    fontFamily:FONT_BODY, fontSize:22, fontWeight:600,
                    color:C.text, width:80, textAlign:'right',
                  }}
                  autoFocus
                />
                <span style={{ fontSize:15, color:C.subtle }}>kg</span>
              </div>
            </div>
          </div>
        </SubPage>
      )}

      {subPage === 'attivita' && (
        <SubPage title="Livello di attivita" onBack={saveSubPage}>
          <div style={{ background:'#fff', borderRadius:16, border:'1px solid rgba(0,0,0,0.06)', overflow:'hidden', padding:'0 16px' }}>
            {ATTIVITA_OPTIONS.map((opt, i, arr) => (
              <div key={opt.value} style={{ borderBottom: i < arr.length-1 ? '0.5px solid rgba(60,60,67,0.10)' : 'none' }}>
                <RadioRow
                  label={opt.label}
                  sub={opt.sub}
                  selected={fieldDraft.livelloAttivita === opt.value}
                  onSelect={() => setFieldDraft(d => ({ ...d, livelloAttivita: opt.value }))}
                />
              </div>
            ))}
          </div>
        </SubPage>
      )}

      {subPage === 'science' && (
        <SubPage title="Come funziona" onBack={() => setSubPage(null)}>
          <SciencePage />
        </SubPage>
      )}

      {subPage === 'scienceTest' && process.env.NODE_ENV === 'development' && (
        <SubPage title="Test scienze" onBack={() => setSubPage(null)}>
          <ScienceTestPage />
        </SubPage>
      )}

    </div>
  );
}
